import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

// Get initial currency from localStorage or default to USD
const getInitialCurrency = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('preferred_currency') || 'USD';
  }
  return 'USD';
};

export const AppProvider = ({ children }) => {
  // Currency preference (persisted to localStorage)
  const [currency, setCurrencyState] = useState(getInitialCurrency);
  
  // Persist currency to localStorage when it changes
  const setCurrency = useCallback((newCurrency) => {
    setCurrencyState(newCurrency);
    if (typeof window !== 'undefined') {
      localStorage.setItem('preferred_currency', newCurrency);
    }
  }, []);

  // Configuration state
  const [configuration, setConfiguration] = useState({
    lei_measure_id: 'training_performance',
    kpi_ids: ['productivity'], // Changed from kpi_id to kpi_ids (array)
    lei_improvement: 5,
    elasticity_scenario: 'moderate',
    intervention_cost: 100000,
    time_horizon_months: 12,
    current_maturity: 50,
    dampening_factors: {
      adoption_speed: 0.3,       // Risk likelihood (0-1): lower = less risk
      leadership_alignment: 0.2, // Risk likelihood (0-1): lower = less risk
      operational_readiness: 0.25, // Risk likelihood (0-1): lower = less risk
    },
    headcount: 1000,
    revenue_per_employee: 150000,
    // Capability Transfer Factors (new)
    manager_support: 'informed',
    job_alignment: 'role_relevant',
    time_to_apply: '2_4_weeks',
    learner_population: null, // If null, defaults to headcount
  });

  // Results state
  const [results, setResults] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isGeneratingNarrative, setIsGeneratingNarrative] = useState(false);
  
  // Current scenario info (for tracking loaded scenarios)
  const [currentScenario, setCurrentScenario] = useState(null);
  
  // Parent scenario info (for tracking re-runs and linking scenarios)
  const [parentScenario, setParentScenario] = useState(null);

  // Reference data
  const [leiMeasures, setLEIMeasures] = useState([]);
  const [kpis, setKPIs] = useState([]);

  // Update configuration
  const updateConfiguration = useCallback((updates) => {
    setConfiguration((prev) => ({ ...prev, ...updates }));
  }, []);

  // Update dampening factors
  const updateDampeningFactor = useCallback((factor, value) => {
    setConfiguration((prev) => ({
      ...prev,
      dampening_factors: {
        ...prev.dampening_factors,
        [factor]: value,
      },
    }));
  }, []);

  // Clear results
  const clearResults = useCallback(() => {
    setResults(null);
    setNarrative(null);
    setCurrentScenario(null);
    setParentScenario(null);
  }, []);

  // Load a scenario (configuration + results)
  const loadScenario = useCallback((scenario, isRerun = false) => {
    if (scenario.configuration) {
      setConfiguration(scenario.configuration);
    }
    if (scenario.results) {
      setResults(scenario.results);
    }
    if (scenario.narrative) {
      setNarrative(scenario.narrative);
    }
    
    // If this is a re-run, track the parent scenario for linking
    if (isRerun) {
      setParentScenario({
        id: scenario.id,
        name: scenario.name,
        scenario_group_id: scenario.scenario_group_id || scenario.id,
        analysis_type: scenario.analysis_type || 'single',
      });
      setCurrentScenario(null); // Clear current since we're creating a new version
    } else {
      setCurrentScenario({
        id: scenario.id,
        name: scenario.name,
        share_token: scenario.share_token,
        scenario_group_id: scenario.scenario_group_id,
      });
      setParentScenario(null);
    }
  }, []);

  // Get selected LEI measure details
  const getSelectedLEIMeasure = useCallback(() => {
    return leiMeasures.find((m) => m.id === configuration.lei_measure_id);
  }, [leiMeasures, configuration.lei_measure_id]);

  // Get selected KPI details (returns array for multi-select)
  const getSelectedKPIs = useCallback(() => {
    return kpis.filter((k) => configuration.kpi_ids?.includes(k.id));
  }, [kpis, configuration.kpi_ids]);

  const value = {
    // Currency
    currency,
    setCurrency,
    // Configuration
    configuration,
    setConfiguration,
    updateConfiguration,
    updateDampeningFactor,
    results,
    setResults,
    narrative,
    setNarrative,
    isCalculating,
    setIsCalculating,
    isGeneratingNarrative,
    setIsGeneratingNarrative,
    leiMeasures,
    setLEIMeasures,
    kpis,
    setKPIs,
    clearResults,
    loadScenario,
    currentScenario,
    setCurrentScenario,
    parentScenario,
    setParentScenario,
    getSelectedLEIMeasure,
    getSelectedKPIs,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
